import React from "react";
import { useImportProgress, ImportOperation, TaxStage } from "@/hooks/useImportProgress";
import { useLatestTransactions, LatestTransaction } from "@/hooks/useLatestTransactions";
import { cn } from "@/utils";
import {
  Band,
  Callout,
  DHead,
  Fig,
  Lab,
  Ring,
  Stage,
  StatBand,
  Tag,
} from "@/components/ms";

interface ImportProgressPanelProps {
  exchangeId: string;
  exchangeName: string;
  variant: "inline" | "modal" | "banner";
}

type OpState = ImportOperation["state"] | TaxStage["state"];

/** The kit's Stage has three marks; failed/retrying read as live-but-wrong, coloured in the title. */
function stageState(state: OpState): "done" | "live" | "wait" {
  if (state === "completed") return "done";
  if (state === "in_progress" || state === "retrying") return "live";
  return "wait";
}

// --- ETA formatter ---

function formatEta(ms: number | null): string {
  if (ms === null) return "Estimating…";
  if (ms <= 0) return "Almost done";
  const secs = Math.ceil(ms / 1000);
  if (secs < 60) return `~${secs}s left`;
  const mins = Math.ceil(secs / 60);
  if (mins < 60) return `~${mins} min left`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `~${hrs}h ${remainMins}m left`;
}

// --- Time ago helper ---

function timeAgo(timestamp: number | string): string {
  const ms = typeof timestamp === "string" ? new Date(timestamp).getTime() : timestamp;
  // If timestamp is in seconds (unix), convert to ms
  const tsMs = ms < 1e12 ? ms * 1000 : ms;
  const diff = Date.now() - tsMs;
  if (diff < 60_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// --- Transaction preview section ---

function TransactionPreview({
  transactions,
  totalImported,
}: {
  transactions: LatestTransaction[];
  totalImported: number;
}) {
  if (transactions.length === 0) return null;

  return (
    <div>
      <DHead label="Landing now" right={`${totalImported.toLocaleString()} total`} />
      {transactions.map((tx, i) => (
        <Fig
          key={`${tx.pair}-${tx.createdAt}-${i}`}
          label={tx.pair}
          sub={timeAgo(tx.timestamp || tx.createdAt)}
          tag={
            <Tag
              tone={
                tx.type === "buy" || tx.type === "deposit"
                  ? "pos"
                  : tx.type === "sell" || tx.type === "withdrawal"
                    ? "neg"
                    : "neu"
              }
            >
              {tx.type}
            </Tag>
          }
          value={
            tx.amount && tx.asset
              ? `${parseFloat(tx.amount).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${tx.asset}`
              : undefined
          }
        />
      ))}
    </div>
  );
}

// --- Banner variant (the status strip under the header) ---

function BannerVariant({
  exchangeName,
  progress,
}: {
  exchangeName: string;
  progress: ReturnType<typeof useImportProgress>;
}) {
  if (progress.phase === "idle") return null;

  const failed = progress.phase === "failed";
  const done = progress.phase === "completed";

  return (
    <Band
      left={
        <>
          <span
            className={cn(
              "block h-[7px] w-[7px] flex-none rounded-full",
              failed ? "bg-ms-neg" : done ? "bg-ms-green" : "animate-pulse bg-ms-rule"
            )}
          />
          <Lab className="truncate">
            {done
              ? `${exchangeName} — import complete`
              : failed
                ? `${exchangeName} — ${progress.error?.message || "import failed"}`
                : `Importing ${exchangeName}${
                    progress.currentOperation ? ` · ${progress.currentOperation.label}` : ""
                  }`}
          </Lab>
        </>
      }
      right={
        <Lab className={failed ? "text-ms-neg" : "text-ms-dim"}>
          {done
            ? `${progress.totalRecords.toLocaleString()} records`
            : failed
              ? "Needs you"
              : `${Math.round(progress.overallProgress)}% · ${formatEta(progress.eta)}`}
        </Lab>
      }
    />
  );
}

// --- Inline / Modal variant (the running job, as a document) ---

function DetailVariant({
  exchangeId,
  exchangeName,
  progress,
  variant,
}: {
  exchangeId: string;
  exchangeName: string;
  progress: ReturnType<typeof useImportProgress>;
  variant: "inline" | "modal";
}) {
  const isCompact = variant === "modal";
  const isActive = progress.phase === "importing" || progress.phase === "validating" || progress.phase === "calculating_taxes";
  const { data: latestTxns } = useLatestTransactions(exchangeId, isActive);

  if (progress.phase === "idle") return null;

  const failed = progress.phase === "failed";
  const done = progress.phase === "completed";
  const stagesDone =
    progress.operations.filter((op) => op.state === "completed").length +
    progress.taxStages.filter((s) => s.state === "completed").length;
  const stagesTotal = progress.operations.length + progress.taxStages.length;

  return (
    <div className="flex flex-col gap-[var(--gap)]">
      {/* Ring + the honest job copy */}
      <div className="flex items-center gap-[18px]">
        <Ring
          pct={progress.overallProgress}
          size={isCompact ? 68 : 84}
          stroke={isCompact ? 7 : 8}
        />
        <div className="min-w-0">
          <h2 className="text-[21px] font-semibold tracking-[-0.03em]">
            {done
              ? `${exchangeName} imported.`
              : failed
                ? `${exchangeName} stopped.`
                : progress.phase === "calculating_taxes"
                  ? `Working out ${exchangeName}'s tax.`
                  : `Importing ${exchangeName}.`}
          </h2>
          <p className="mt-2 max-w-[44em] text-[13.5px] leading-[1.55] text-ms-mute">
            {failed
              ? progress.error?.message || "We couldn't finish this import."
              : done
                ? `${progress.totalRecords.toLocaleString()} records are in and your number is updated.`
                : "You can leave this page — the job keeps running and we'll tell you when your number changes. Nothing in the app waits on it."}
          </p>
        </div>
      </div>

      {!done && !failed && (
        <Callout>
          <b>Nothing waits on you.</b> Add another source or answer open questions while this
          runs — we recalculate as each piece lands.
        </Callout>
      )}

      {/* Progress stages */}
      {(progress.operations.length > 0 || progress.taxStages.length > 0) && (
        <div>
          <DHead
            label="Progress"
            right={done ? "Complete" : `${Math.round(progress.overallProgress)}%`}
          />
          <div className={cn("mt-1.5", isCompact && "max-h-[280px] overflow-y-auto")}>
            {progress.operations.map((op) => (
              <Stage
                key={op.id}
                state={stageState(op.state)}
                title={
                  op.state === "failed" ? (
                    <span className="text-ms-neg">{op.label}</span>
                  ) : (
                    op.label
                  )
                }
                sub={
                  op.state === "failed"
                    ? "Stopped"
                    : op.state === "completed"
                      ? "Done"
                      : op.state === "retrying"
                        ? "Retrying"
                        : // A running operation reports 0 records until its first
                          // page lands — it is still working, not waiting.
                          op.state === "in_progress"
                          ? op.recordsProcessed > 0
                            ? `${op.recordsProcessed.toLocaleString()}${
                                op.estimatedTotal
                                  ? ` of ${op.estimatedTotal.toLocaleString()}`
                                  : ""
                              } pulled`
                            : "Working…"
                          : "Waiting"
                }
                progress={op.state === "in_progress" ? op.progress : undefined}
              />
            ))}
            {progress.taxStages.map((stage) => (
              <Stage
                key={stage.id}
                state={stageState(stage.state)}
                title={
                  stage.state === "failed" ? (
                    <span className="text-ms-neg">{stage.label}</span>
                  ) : (
                    stage.label
                  )
                }
                sub={
                  stage.state === "completed" && stage.durationMs
                    ? `${(stage.durationMs / 1000).toFixed(1)}s`
                    : stage.state === "in_progress"
                      ? formatEta(progress.eta)
                      : "Waiting"
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Found so far */}
      <div>
        <DHead label="Found so far" right={done ? "Final" : "Live"} />
        <StatBand
          className="mt-3"
          items={[
            { label: "Transactions", value: progress.totalRecords.toLocaleString() },
            { label: "Complete", value: `${Math.round(progress.overallProgress)}%` },
            {
              label: "Steps done",
              value: stagesTotal > 0 ? `${stagesDone}/${stagesTotal}` : "—",
            },
            {
              label: done ? "Status" : "Time left",
              value: failed ? "Stopped" : done ? "Done" : formatEta(progress.eta),
              tone: failed ? "neg" : undefined,
            },
          ]}
        />
      </div>

      {/* Error state */}
      {failed && progress.error && (
        <Callout tone="neg">
          <b>{progress.error.message}</b>
          {progress.error.retryable && (
            <span className="mt-1 block">This often clears on retry — start the sync again.</span>
          )}
        </Callout>
      )}

      {/* Live transaction preview */}
      {latestTxns && latestTxns.transactions.length > 0 && (
        <TransactionPreview
          transactions={latestTxns.transactions}
          totalImported={latestTxns.totalImported}
        />
      )}
    </div>
  );
}

// --- Main component ---

export const ImportProgressPanel: React.FC<ImportProgressPanelProps> = ({
  exchangeId,
  exchangeName,
  variant,
}) => {
  const progress = useImportProgress(exchangeId);

  if (progress.phase === "idle") return null;

  if (variant === "banner") {
    return <BannerVariant exchangeName={exchangeName} progress={progress} />;
  }

  return (
    <DetailVariant
      exchangeId={exchangeId}
      exchangeName={exchangeName}
      progress={progress}
      variant={variant}
    />
  );
};
